import React from 'react';
import { Document, Page, Text, View, StyleSheet, Font, Svg, Path, Image } from '@react-pdf/renderer';
import { colors, spacing } from '../../utils/styling';
import { QUESTIONS } from '../../utils/quizinfo/questions';
import { CATEGORY_SECTIONS } from '../../utils/category_mappings';
import NunitoRegular from '../../fonts/Nunito-Regular.ttf';
import NunitoBold from '../../fonts/Nunito-Bold.ttf';

Font.register({
  family: 'Nunito',
  fonts: [
    { src: NunitoRegular, fontWeight: 'normal' },
    { src: NunitoBold, fontWeight: 'bold' }
  ]
});

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 40,
    fontFamily: 'Nunito',
  },
  // Main container styles
  mainContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 20,
  },
  // Header styles
  header: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 30,
    paddingBottom: 20,
    borderBottom: '2px solid #e0e0e0',
  },
  logo: {
    height: 50,
    width: 'auto',
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  userName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colors.dark,
    fontFamily: 'Nunito',
    marginBottom: 4,
  },
  userDate: {
    fontSize: 12,
    color: colors.gray[600],
    fontFamily: 'Nunito',
  },
  contentContainer: {
    width: '100%',
    maxWidth: 800,
  },
  // Card styles
  card: {
    backgroundColor: colors.white,
    borderRadius: 16,
    padding: 20,
    marginBottom: 20,
    border: '3px solid black',
  },
  // Header card styles
  headerCard: {
    alignItems: 'center',
    textAlign: 'center',
  },
  primaryTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.dark,
    fontFamily: 'Nunito',
    marginBottom: 10,
  },
  dominantCategoryTitle: {
    fontSize: 48,
    fontWeight: 'bold',
    color: colors.dark,
    fontFamily: 'Nunito',
    marginBottom: 10,
  },
  subtext: {
    fontSize: 16,
    color: colors.gray[700],
    marginBottom: 20,
    fontFamily: 'Nunito',
  },
  overview: {
    fontSize: 14,
    color: colors.gray[700],
    marginBottom: 15,
    fontFamily: 'Nunito',
    textAlign: 'center',
  },
  advisorNotice: {
    backgroundColor: colors.green + '20',
    padding: 8,
    borderRadius: 8,
    marginTop: 8,
    alignSelf: 'center',
  },
  advisorText: {
    color: colors.green,
    textAlign: 'center',
    fontSize: 14,
    fontFamily: 'Nunito',
  },
  // Graph card styles
  graphCard: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  categorySection: {
    width: '50%',
    padding: 20,
  },
  categoryItem: {
    marginBottom: 24,
  },
  categoryHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  categoryName: {
    fontFamily: 'Nunito',
    fontSize: 12,
  },
  categoryPercentage: {
    color: '#717171',
    fontWeight: 'normal',
    fontSize: 10,
  },
  progressBar: {
    height: 8,
    backgroundColor: colors.gray[200],
    borderRadius: 4,
  },
  graphSection: {
    width: '50%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  // Pie chart styles
  pieChartContainer: {
    width: 150,
    height: 150,
    position: 'relative',
    marginBottom: 10,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  pieChartLabel: {
    fontSize: 8,
    color: colors.gray[600],
    textAlign: 'center',
    marginTop: 15,
    fontFamily: 'Nunito',
  },
  legendContainer: {
    marginTop: 10,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 3,
  },
  legendColor: {
    width: 8,
    height: 8,
    marginRight: 6,
  },
  legendText: {
    fontSize: 8,
    color: colors.gray[700],
    fontFamily: 'Nunito',
  },
  // Detailed analysis sections
  traitsSection: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
    color: colors.dark,
    fontFamily: 'Nunito',
    textAlign: 'center',
  },
  traitsContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 10,
  },
  trait: {
    backgroundColor: colors.gray[100],
    padding: 6,
    borderRadius: 4,
    fontSize: 10,
    color: colors.gray[700],
    fontFamily: 'Nunito',
  },
  // Strengths and weaknesses sections
  strengthsSection: {
    backgroundColor: '#ebf7ed',
    borderRadius: 16,
    padding: 20,
    marginBottom: 20,
    border: '3px solid #b1ddb4',
  },
  weaknessesSection: {
    backgroundColor: '#fffbe8',
    borderRadius: 16,
    padding: 20,
    marginBottom: 20,
    border: '3px solid #f4e67d',
  },
  strength: {
    backgroundColor: colors.success + '20',
    padding: 6,
    borderRadius: 4,
    fontSize: 10,
    color: colors.success,
    fontFamily: 'Nunito',
    marginBottom: 4,
  },
  weakness: {
    backgroundColor: colors.danger + '20',
    padding: 6,
    borderRadius: 4,
    fontSize: 10,
    color: colors.danger,
    fontFamily: 'Nunito',
    marginBottom: 4,
  },
  // Sub-personality section
  subPersonalitySection: {
    border: '3px solid black',
    borderRadius: 16,
    backgroundColor: colors.white,
    padding: 20,
    marginBottom: 20,
  },
  subPersonalityTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colors.dark,
    fontFamily: 'Nunito',
    textAlign: 'center',
    marginBottom: 10,
  },
  subPersonalityName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colors.dark,
    fontFamily: 'Nunito',
    textAlign: 'center',
    marginBottom: 10,
  },
  subPersonalityOverview: {
    fontFamily: 'Nunito',
    fontWeight: 'normal',
    fontSize: 12,
    textAlign: 'center',
  },
  // Growth opportunities and behavioral concepts
  growthSection: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 20,
    marginBottom: 20,
    border: '3px solid black',
  },
  behavioralSection: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 20,
    marginBottom: 20,
    border: '3px solid black',
  },
  growthItem: {
    backgroundColor: colors.green + '20',
    padding: 6,
    borderRadius: 4,
    fontSize: 10,
    color: colors.green,
    fontFamily: 'Nunito',
    marginBottom: 4,
  },
  behavioralItem: {
    backgroundColor: colors.pink + '20',
    padding: 6,
    borderRadius: 4,
    fontSize: 10,
    color: colors.pink,
    fontFamily: 'Nunito',
    marginBottom: 4,
  },
  // Personal information section
  personalInfoSection: {
    backgroundColor: '#f8f9fa',
    borderRadius: 16,
    padding: 20,
    marginBottom: 20,
    border: '2px solid #e9ecef',
  },
  personalInfoTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 15,
    color: colors.dark,
    fontFamily: 'Nunito',
    textAlign: 'center',
  },
  personalInfoGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  personalInfoItem: {
    backgroundColor: colors.white,
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
    border: '1px solid #dee2e6',
    width: '48%',
  },
  personalInfoQuestion: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 4,
    color: colors.gray[600],
    fontFamily: 'Nunito',
  },
  personalInfoAnswer: {
    fontSize: 12,
    color: colors.dark,
    fontFamily: 'Nunito',
    fontWeight: 'bold',
  },
  // Responses section
  responsesSection: {
    marginTop: 30,
  },
  responsesTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 15,
    color: colors.dark,
    fontFamily: 'Nunito',
  },
  responseRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 10,
    padding: 10,
    backgroundColor: colors.gray[100],
    borderRadius: 4,
    minHeight: 60,
  },
  specificResponseRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 10,
    padding: 10,
    backgroundColor: colors.gray[100],
    borderRadius: 4,
    minHeight: 80,
  },
  responseQuestion: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 4,
    color: colors.dark,
    fontFamily: 'Nunito',
  },
  responseAnswer: {
    fontSize: 10,
    color: colors.gray[700],
    fontFamily: 'Nunito',
    flex: 1,
  },
  responseCategoryContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    minWidth: 80,
    justifyContent: 'flex-end',
    height: '100%',
  },
  specificResponseCategoryContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    minWidth: 80,
    justifyContent: 'flex-end',
  },
  categoryCircle: {
    width: 10,
    height: 10,
    borderRadius: 5,
    marginRight: 6,
  },
  responseCategoryLabel: {
    fontSize: 9,
    color: colors.gray[500],
    fontFamily: 'Nunito',
  },
  // Exercises section styles
  exercisesSection: {
    marginTop: 30,
  },
  exercisesTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 15,
    color: colors.dark,
    fontFamily: 'Nunito',
  },
  exerciseRow: {
    flexDirection: 'column',
    marginBottom: 10,
    padding: 10,
    backgroundColor: colors.gray[100],
    borderRadius: 4,
    minHeight: 200,
  },
  exerciseQuestion: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 4,
    color: colors.dark,
    fontFamily: 'Nunito',
  },
  exerciseAnswer: {
    fontSize: 10,
    color: colors.gray[700],
    fontFamily: 'Nunito',
    flex: 1,
    marginBottom: 4,
  },
  exerciseTypeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  exerciseTypeLabel: {
    fontSize: 9,
    color: colors.gray[500],
    fontFamily: 'Nunito',
  },
  footer: {
    position: 'absolute',
    bottom: 40,
    left: 40,
    right: 40,
    textAlign: 'center',
    fontSize: 10,
    color: colors.gray[500],
    fontFamily: 'Nunito',
  },
});

interface Exercise {
  id: number;
  title: string;
  header: string;
  intro: string;
  prompt: string;
  tips: string[];
  footer: string;
}

interface PersonalityTestPDFProps {
  answers: number[];
  categoryScores: Record<string, number>;
  dominantCategory: string;
  personalInfo?: Record<string, string>;
  exercises?: Exercise[];
  userName?: string;
  overview?: boolean;
}

// Function to get exercise type based on exercise ID
function getExerciseType(id: number): string {
  switch (id) {
    case 1:
      return "Reflective";
    case 2:
      return "Visualization/Mapping";
    case 3:
      return "Action Planning";
    case 4:
      return "Behavior/Pattern Tracking";
    case 5:
      return "Interactive Insight Activity";
    default:
      return "Exercise";
  }
}

const PersonalityTestPDF: React.FC<PersonalityTestPDFProps> = ({
  answers,
  categoryScores,
  dominantCategory,
  personalInfo = {},
  exercises = [],
  userName = '',
  overview = false,
}) => {
  const total = Object.values(categoryScores).reduce((a, b) => a + b, 0);
  const percentages = Object.entries(categoryScores).map(([category, value]) => ({
    category,
    percentage: Math.round((value / total) * 100),
  }));

  // Get the category data from the mapping
  const categoryData = CATEGORY_SECTIONS[dominantCategory];
  const personalityQuestions = QUESTIONS.filter(q => q.type === "personality");
  const profileQuestions = QUESTIONS.filter(q => q.type === "profile");

  // Category colors
  const CATEGORY_COLORS: Record<string, string> = {
    'Dream Chaser': "#00A4EF",
    'Wealth Architect': "#00B3C2",
    'Safety Seeker': "#076652",
    'Generous Altruist': "#9933FF",
    'Freedom Finder': "#FF00FF",
  };


  // Sort categories by percentage (descending)
  const sortedCategories = Object.keys(categoryScores)
    .sort((a, b) => (categoryScores[b] ?? 0) - (categoryScores[a] ?? 0));
  

  // Create pie chart data
  const pieChartData = sortedCategories.map(category => ({
    category,
    percentage: categoryScores[category] !== undefined ? Math.round(categoryScores[category]) : 0
  })).filter(item => item.percentage > 0);

  // Pie Chart Component for PDF
  const PieChart = () => {
    const size = 120;
    const radius = size / 2;
    const center = radius;
    
    let currentAngle = 0;
    const paths: Array<{ path: string; color: string; category: string; percentage: number }> = [];
    
    pieChartData.forEach(({ category, percentage }) => {
      if (percentage <= 0) return;
      
      const angle = (percentage / 100) * 2 * Math.PI;
      const startAngle = currentAngle;
      const endAngle = currentAngle + angle;
      
      // Calculate start and end points
      const x1 = center + radius * Math.cos(startAngle);
      const y1 = center + radius * Math.sin(startAngle);
      const x2 = center + radius * Math.cos(endAngle);
      const y2 = center + radius * Math.sin(endAngle);
      
      // Determine if arc should be large (more than 180 degrees)
      const largeArcFlag = angle > Math.PI ? 1 : 0;
      
      // Create SVG path
      const path = [
        `M ${center} ${center}`,
        `L ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
        'Z'
      ].join(' ');
      
      paths.push({
        path,
        color: CATEGORY_COLORS[category] || '#000',
        category,
        percentage
      });
      
      currentAngle = endAngle;
    });
    
    return (
      <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {paths.map(({ path, color, category }, index) => (
          <Path
            key={`${category}-${index}`}
            d={path}
            fill={color}
            stroke="#fff"
            strokeWidth={1}
          />
        ))}
      </Svg>
    );
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.mainContainer}>
          <View style={styles.header}>
            <View>
              <Image src="https://qliamhiuthuvtbjfiwzb.supabase.co/storage/v1/object/public/quiz-pdf-files/logo.png" style={styles.logo} />
            </View>
            <View style={styles.userInfo}>
              <Text style={styles.userName}>{userName || 'User'}</Text>
              <Text style={styles.userDate}>
                {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </Text>
            </View>
          </View>
          <View style={styles.contentContainer}>
            
            {/* Header Card - "You are primarily a..." */}
            <View style={[styles.card, styles.headerCard]}>
              <Text style={styles.primaryTitle}>
                You are primarily a{(dominantCategory.toLowerCase().startsWith('a') || 
                                  dominantCategory.toLowerCase().startsWith('e') || 
                                  dominantCategory.toLowerCase().startsWith('i') ||
                                  dominantCategory.toLowerCase().startsWith('o') ||
                                  dominantCategory.toLowerCase().startsWith('u')) ? 'n ' : ''}
              </Text>
              <Text style={styles.dominantCategoryTitle}>
                {dominantCategory}
              </Text>
              <Text style={styles.subtext}>
                {categoryData?.Subtext}
              </Text>
              <Text style={styles.overview}>
                {categoryData?.Overview}
              </Text>
            </View>

            {/* Graph Card */}
            <View style={[styles.card, styles.graphCard]} wrap={false}>
              {/* Categories and percentages */}
              <View style={styles.categorySection}>
                {sortedCategories.map((category) => (
                  <View key={category} style={styles.categoryItem}>
                    <View style={styles.categoryHeader}>
                      <Text style={styles.categoryName}>{category}</Text>
                      <Text style={styles.categoryPercentage}>
                        {categoryScores[category] !== undefined ? Math.round(categoryScores[category]) : 0}%
                      </Text>
                    </View>
                    <View style={styles.progressBar}>
                      <View style={{
                        width: `${categoryScores[category] !== undefined ? Math.round(categoryScores[category]) : 0}%`,
                        height: 8,
                        backgroundColor: CATEGORY_COLORS[category] || colors.primary,
                        borderRadius: 4,
                      }} />
                    </View>
                  </View>
                ))}
              </View>
              {/* Graph section */}
              <View style={styles.graphSection}>
                <View style={styles.pieChartContainer}>
                  <PieChart />
                  <Text style={styles.pieChartLabel}>Personality Breakdown</Text>
                </View>
              </View>
            </View>

            {/* Traits Section */}
            <View style={[styles.card, styles.traitsSection]} wrap={false}>
              <Text style={styles.sectionTitle}>Dominant traits</Text>
              <View style={styles.traitsContainer}>
                {categoryData?.DominantTraits.map((trait, i) => (
                  <Text key={i} style={styles.trait}>{trait}</Text>
                ))}
              </View>
            </View>

            {!overview && (
            <>
            {/* Strengths Section */}
            <View style={[styles.card, styles.strengthsSection]} wrap={false}>
              <Text style={styles.sectionTitle}>Strengths</Text>
              <View style={styles.traitsContainer}>
                {categoryData?.Strengths.map((strength, i) => (
                  <Text key={i} style={styles.strength}>{strength}</Text>
                ))}
              </View>
            </View>

            {/* Weaknesses Section */}
            <View style={[styles.card, styles.weaknessesSection]} wrap={false}>
              <Text style={styles.sectionTitle}>Weaknesses</Text>
              <View style={styles.traitsContainer}>
                {categoryData?.Weaknesses.map((weakness, i) => (
                  <Text key={i} style={styles.weakness}>{weakness}</Text>
                ))}
              </View>
            </View>

            {/* The Subset Personality block was REMOVED on 2026-08-20 (ClickUp
                86bb0f9vz). McKayla: "They don't need to know if they're a
                subtype." This report now covers the core personality only. */}

            {/* Growth Opportunities Section */}
            <View style={[styles.card, styles.growthSection]} wrap={false}>
              <Text style={styles.sectionTitle}>Growth Opportunities</Text>
              <View style={styles.traitsContainer}>
                {categoryData?.GrowthOpportunities.map((opportunity, i) => (
                  <Text key={i} style={styles.growthItem}>{opportunity}</Text>
                ))}
              </View>
            </View>

            {/* Behavioral Concepts Section */}
            <View style={[styles.card, styles.behavioralSection]} wrap={false}>
              <Text style={styles.sectionTitle}>Behavioral Concepts</Text>
              <View style={styles.traitsContainer}>
                {categoryData?.BehavioralConcepts.map((concept, i) => (
                  <Text key={i} style={styles.behavioralItem}>{concept}</Text>
                ))}
              </View>
            </View>

            {/* Exercises Section */}
            {exercises.length > 0 && (
              <View style={styles.exercisesSection}>
                <Text style={styles.exercisesTitle}>Personalized Exercises</Text>
                                  {exercises.map((exercise, index) => (
                    <View key={exercise.id} style={styles.exerciseRow} wrap={false}>
                      <View style={styles.exerciseTypeContainer}>
                        <View style={[styles.categoryCircle, { backgroundColor: colors.primary }]} />
                        <Text style={styles.exerciseTypeLabel}>{getExerciseType(exercise.id)}</Text>
                      </View>
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <Text style={styles.exerciseQuestion}>{exercise.header}</Text>
                        <Text style={styles.exerciseAnswer}>{exercise.intro}</Text>
                        <Text style={styles.exerciseAnswer}>Prompt: {exercise.prompt}</Text>
                        <Text style={styles.exerciseAnswer}>Tips: {exercise.tips.join(', ')}</Text>
                        <Text style={styles.exerciseAnswer}>{exercise.footer}</Text>
                      </View>
                    </View>
                  ))}
              </View>
            )}

            {/* Responses Section */}
            <View style={styles.responsesSection}>
              <Text style={styles.responsesTitle}>Your Responses</Text>
              {personalityQuestions.map((question, index) => {
                // Index against QUESTIONS, not against the filtered personality list:
                // `answers` is built positionally over the FULL question set, so using
                // the filtered index would shift every row once the two lists diverge.
                const answer = answers[QUESTIONS.indexOf(question)];
                // -1 means the stored answer matched no current option (legacy record).
                // Skip it rather than printing option 0, which would render a confident
                // wrong answer. See utils/quizinfo/legacyAnswers.ts (ClickUp 86bb0fwu5).
                if (answer === undefined || answer < 0) return null;
                const selectedOption = question.options[answer];
                if (!selectedOption || selectedOption === undefined) return null;
                
                // Determine color based on category
                const catColor = CATEGORY_COLORS[selectedOption.category!];
                
                return (
                  <View key={index} style={styles.responseRow} wrap={false}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.responseQuestion}>{question.question}</Text>
                      <Text style={styles.responseAnswer}>{selectedOption.text}</Text>
                    </View>
                    <View style={styles.responseCategoryContainer}>
                      <View style={[styles.categoryCircle, { backgroundColor: catColor }]} />
                      <Text style={styles.responseCategoryLabel}>{selectedOption.category}</Text>
                    </View>
                  </View>
                );
              })}
            </View>
            <View style={styles.responsesSection}>
              {/* The sub-personality responses list was removed on 2026-08-20
                  with the sub-personality quiz (ClickUp 86bb0f9vz). */}

              {/* Personal Information Section */}
              {personalInfo && Object.keys(personalInfo).length > 0 && (
                <View style={[styles.card, styles.personalInfoSection]} wrap={false}>
                  <Text style={styles.personalInfoTitle}>Personal Information</Text>
                  <View style={styles.personalInfoGrid}>
                    {Object.entries(personalInfo).map(([question, answer], index) => (
                      <View key={index} style={styles.personalInfoItem}>
                        <Text style={styles.personalInfoQuestion}>{question}</Text>
                        <Text style={styles.personalInfoAnswer}>{answer}</Text>
                      </View>
                    ))}
                  </View>
                </View>
              )}
            </View>
            </>
            )}
          </View>
        </View>

        {/* Footer */}
        <Text style={styles.footer}>
          Generated on {new Date().toLocaleDateString()} • Financial Personality Assessment
        </Text>
      </Page>
    </Document>
  );
};

export default PersonalityTestPDF; 